import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { inputDollarsToCents } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { isSuperAdmin } from '@/lib/permissions';
import {
  ITEM_KIND_COLORS,
  partitionItemKinds,
  useCreateItem,
  useCreateItemKind,
  useItemKinds,
  useItems,
  type ItemKind,
  type ItemKindColor,
  type ItemStatus,
} from '@/features/inventory/hooks';
import { ITEM_STATUS_OPTIONS as STATUS_OPTIONS } from '@/features/inventory/itemStatus';

interface NewItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called once the item row has been created. The Items page wires
   * this to navigate the user into the new item's detail page; tests
   * can override.
   */
  onCreated?: (itemId: string) => void;
  /**
   * Slug of the kind to pre-select when the dialog opens. The per-kind
   * variant of the Items page (`/catalog/items/kind/:slug`) passes
   * this so an operator who clicked "Add item" from inside (e.g.)
   * the Raw materials view doesn't have to re-pick the kind to land
   * back where they came from.
   *
   * Falls back to `'finished_good'` (the historical default) when
   * unset or when the slug isn't in the kinds catalog at submit time.
   */
  defaultKind?: string;
  /**
   * Open the new item's detail page after creating it. Defaults to
   * true; pickers that create an item mid-flow (e.g. the BOM builder)
   * turn it off so the operator stays where they are.
   */
  navigateOnCreate?: boolean;
}

/**
 * Tailwind classnames per kind colour token, used by the inline
 * "Create new kind" swatch picker. Mirrors the equivalents in
 * `KindPicker` and `TagsPicker` so the palette renders identically
 * everywhere a kind colour swatch shows up.
 */
const KIND_SWATCH_CLASS: Record<ItemKindColor, string> = {
  slate: 'bg-slate-400',
  red: 'bg-red-400',
  orange: 'bg-orange-400',
  amber: 'bg-amber-400',
  green: 'bg-green-400',
  teal: 'bg-teal-400',
  blue: 'bg-blue-400',
  indigo: 'bg-indigo-400',
  purple: 'bg-purple-400',
  pink: 'bg-pink-400',
};

/**
 * "Add item" modal — minimal create form for the item master. We
 * collect just enough to make the row useful in the grid (SKU, name,
 * type, status, optional pricing, lead time, notes); the rest of the
 * 360 view (suppliers, workstations, BOM, documents, photo) is filled
 * in on the detail page once the item exists.
 *
 * Submitting navigates straight to the new item's detail page so the
 * operator can immediately keep editing.
 */
export function NewItemDialog({
  open,
  onOpenChange,
  onCreated,
  defaultKind,
  navigateOnCreate = true,
}: NewItemDialogProps) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isSuper = isSuperAdmin(role);
  const createItem = useCreateItem();
  const createKind = useCreateItemKind();
  // Pull existing items so we can offer their colourways as a datalist.
  // We don't *require* re-using one, but the autocomplete keeps the
  // vocabulary tight and saves a lot of typos.
  const { data: existingItems } = useItems();
  const { data: kindsCatalog, isLoading: kindsLoading } = useItemKinds();

  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [kinds, setKinds] = useState<ItemKind[]>([defaultKind ?? 'finished_good']);
  const [status, setStatus] = useState<ItemStatus>('concept');
  const [baseUom, setBaseUom] = useState('each');
  const [color, setColor] = useState('');
  const [description, setDescription] = useState('');
  const [stdCost, setStdCost] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [leadTimeText, setLeadTimeText] = useState('');
  const [leadTimeWorstCase, setLeadTimeWorstCase] = useState('');
  const [notes, setNotes] = useState('');

  const orderedKindSlugs = useMemo(() => (kindsCatalog ?? []).map((k) => k.slug), [kindsCatalog]);
  const kindSelection = useMemo(() => {
    // Keep the page's default type as the stored primary while it stays
    // checked. Otherwise the first checked type wins, so adding an
    // earlier catalog type doesn't silently replace the one the operator
    // started with.
    const preferred = defaultKind && kinds.includes(defaultKind) ? defaultKind : kinds[0];
    try {
      return partitionItemKinds(kinds, preferred, orderedKindSlugs);
    } catch {
      return null;
    }
  }, [kinds, defaultKind, orderedKindSlugs]);
  // "Components" (slug `raw_material`) is a build/buy part — it isn't sold
  // at retail, so the retail-price field is suppressed unless the item is
  // also a finished good.
  const isComponent = kindSelection?.kind === 'raw_material' && !kinds.includes('finished_good');

  // Inline "Create new kind" form. Visible to super admins only,
  // mirroring the affordance the KindPicker shows in the items grid /
  // detail page so the dialog and the inline pickers feel like the
  // same control.
  const [creatingKind, setCreatingKind] = useState(false);
  const [draftKindLabel, setDraftKindLabel] = useState('');
  const [draftKindColor, setDraftKindColor] = useState<ItemKindColor>('slate');

  // Reset on close so a previous draft doesn't bleed through if the
  // operator hits Esc and then reopens to start over. The kind seed
  // follows `defaultKind` so the per-kind variant of the Items page
  // (e.g. /catalog/items/kind/raw_material) opens a dialog already
  // primed with that kind.
  useEffect(() => {
    if (!open) {
      setSku('');
      setName('');
      setKinds([defaultKind ?? 'finished_good']);
      setStatus('concept');
      setBaseUom('each');
      setColor('');
      setDescription('');
      setStdCost('');
      setListPrice('');
      setLeadTimeText('');
      setLeadTimeWorstCase('');
      setNotes('');
      setCreatingKind(false);
      setDraftKindLabel('');
      setDraftKindColor('slate');
    }
  }, [open, defaultKind]);

  // Drop any checked type that isn't in the catalog (the dialog can
  // open before the catalog loads, or a super admin can remove a
  // custom type). If that leaves nothing checked, fall back to the
  // first catalog row so the insert always satisfies the kind FK.
  useEffect(() => {
    if (!kindsCatalog || kindsCatalog.length === 0) return;
    const valid = new Set(kindsCatalog.map((k) => k.slug));
    setKinds((prev) => {
      const kept = prev.filter((slug) => valid.has(slug));
      if (kept.length === prev.length) return prev;
      return kept.length > 0 ? kept : [kindsCatalog[0].slug];
    });
  }, [kindsCatalog]);

  const colorOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const it of existingItems ?? []) {
      if (it.color) seen.add(it.color);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [existingItems]);

  /**
   * Persist a new kind row and select it for this draft. RLS gates
   * the underlying mutation to super_admin; the surfacing affordance
   * is also `isSuper`-gated above, so this only runs in a permitted
   * context.
   */
  const submitCreateKind = async () => {
    const label = draftKindLabel.trim();
    if (!label) {
      toast.error('Give the new type a name.');
      return;
    }
    try {
      const created = await createKind.mutateAsync({ label, color: draftKindColor });
      setKinds((prev) => (prev.includes(created.slug) ? prev : [...prev, created.slug]));
      setCreatingKind(false);
      setDraftKindLabel('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create kind.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedSku = sku.trim();
    const trimmedName = name.trim();
    if (!trimmedSku) {
      toast.error('SKU is required.');
      return;
    }
    if (!trimmedName) {
      toast.error('Name is required.');
      return;
    }
    if (!kindSelection) {
      toast.error('Pick at least one type.');
      return;
    }

    let stdCostCents: number | null;
    let listPriceCents: number | null;
    try {
      stdCostCents = inputDollarsToCents(stdCost);
      // Components don't carry a retail price, so never send one for them.
      listPriceCents = isComponent ? null : inputDollarsToCents(listPrice);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid price.');
      return;
    }

    // Worst-case lead time is optional; when present it must be whole days.
    let worstCaseDays: number | null = null;
    const worstCaseRaw = leadTimeWorstCase.trim();
    if (worstCaseRaw) {
      const parsed = Number(worstCaseRaw);
      if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
        toast.error('Worst-case lead time must be a whole number of days (0 or more).');
        return;
      }
      worstCaseDays = parsed;
    }

    try {
      const item = await createItem.mutateAsync({
        sku: trimmedSku,
        name: trimmedName,
        kind: kindSelection.kind,
        additional_kinds: kindSelection.additional_kinds,
        status,
        base_uom: baseUom,
        color: color.trim() || null,
        description: description.trim() || null,
        std_cost_cents: stdCostCents,
        list_price_cents: listPriceCents,
        lead_time_text: leadTimeText.trim() || null,
        lead_time_worst_case_days: worstCaseDays,
        notes: notes.trim() || null,
      });
      toast.success(`Created “${item.name}”`);
      onOpenChange(false);
      onCreated?.(item.id);
      if (navigateOnCreate) navigate(`/catalog/items/${item.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create item.';
      toast.error(msg);
    }
  };

  const submitting = createItem.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add item</DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="item-sku">
                SKU <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="item-sku"
                autoFocus
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="e.g. CT-RNW-MAN-BLK-02"
                maxLength={64}
                className="font-mono text-xs"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-name">
                Name <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="item-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Renew Manual Black"
                maxLength={200}
                required
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <Label id="item-kind-label">Type</Label>
                {/* Super-admin-only quick action: mints a new type in
                    the org's catalog and selects it for this draft.
                    Mirrors the inline-create flow in KindPicker so an
                    operator can stay in this dialog without bouncing
                    out to the items grid. */}
                {isSuper && !creatingKind && (
                  <button
                    type="button"
                    onClick={() => {
                      setCreatingKind(true);
                      setDraftKindLabel('');
                      setDraftKindColor(
                        ITEM_KIND_COLORS[(kindsCatalog?.length ?? 0) % ITEM_KIND_COLORS.length],
                      );
                    }}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Plus className="h-3 w-3" />
                    New type
                  </button>
                )}
              </div>
              {creatingKind ? (
                <div className="space-y-2 rounded-md border border-dashed border-border p-2">
                  <Input
                    id="item-kind-new"
                    autoFocus
                    value={draftKindLabel}
                    onChange={(e) => setDraftKindLabel(e.target.value)}
                    onKeyDown={(e) => {
                      // Don't let Enter inside this side-form submit
                      // the parent dialog before the kind has been
                      // saved — wire Enter to the create handler
                      // instead so the keyboard flow matches the
                      // KindPicker dropdown.
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void submitCreateKind();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setCreatingKind(false);
                        setDraftKindLabel('');
                      }
                    }}
                    placeholder="e.g. Service, Spare part…"
                    maxLength={64}
                  />
                  <div className="flex flex-wrap items-center gap-1.5">
                    {ITEM_KIND_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setDraftKindColor(c)}
                        aria-label={`Use ${c} colour`}
                        title={c}
                        className={cn(
                          'h-5 w-5 rounded-full border-2 transition-transform hover:scale-110',
                          KIND_SWATCH_CLASS[c],
                          draftKindColor === c ? 'border-foreground' : 'border-transparent',
                        )}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCreatingKind(false);
                        setDraftKindLabel('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void submitCreateKind()}
                      disabled={createKind.isPending || !draftKindLabel.trim()}
                    >
                      {createKind.isPending ? 'Creating…' : 'Create type'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  role="group"
                  aria-labelledby="item-kind-label"
                  className="space-y-0.5 rounded-md border border-border p-2"
                >
                  {kindsLoading ? (
                    <p className="px-1 py-2 text-xs text-muted-foreground">Loading types…</p>
                  ) : (kindsCatalog ?? []).length === 0 ? (
                    <p className="px-1 py-2 text-xs text-muted-foreground">
                      No types yet.{' '}
                      {isSuper ? 'Create one above.' : 'Ask a super admin to add one.'}
                    </p>
                  ) : (
                    (kindsCatalog ?? []).map((k) => {
                      const checked = kinds.includes(k.slug);
                      return (
                        <label
                          key={k.id}
                          className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1.5 hover:bg-secondary/60"
                        >
                          <Checkbox
                            checked={checked}
                            ariaLabel={k.label}
                            className="mt-0.5"
                            onCheckedChange={(next) => {
                              setKinds((prev) => {
                                const has = prev.includes(k.slug);
                                if (next && !has) return [...prev, k.slug];
                                if (!next && has) {
                                  if (prev.length === 1) {
                                    toast.error('An item needs at least one type.');
                                    return prev;
                                  }
                                  return prev.filter((slug) => slug !== k.slug);
                                }
                                return prev;
                              });
                            }}
                          />
                          <span className="block text-sm">{k.label}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as ItemStatus)}>
                <SelectTrigger id="item-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-uom">Base UoM</Label>
              <Input
                id="item-uom"
                value={baseUom}
                onChange={(e) => setBaseUom(e.target.value)}
                placeholder="each"
                maxLength={32}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-color">Color</Label>
              <Input
                id="item-color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="e.g. Black"
                maxLength={64}
                list="item-color-options"
              />
              {colorOptions.length > 0 && (
                <datalist id="item-color-options">
                  {colorOptions.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-cost">Standard cost</Label>
              <Input
                id="item-cost"
                value={stdCost}
                onChange={(e) => setStdCost(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="tabular-nums"
              />
            </div>

            {/* Retail price only applies to sellable kinds — Components
                (raw_material) are build/buy parts, so the field is hidden
                for them. */}
            {!isComponent && (
              <div className="space-y-1.5">
                <Label htmlFor="item-price">Retail price</Label>
                <Input
                  id="item-price"
                  value={listPrice}
                  onChange={(e) => setListPrice(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  className="tabular-nums"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="item-lead-time">Lead time</Label>
              <Input
                id="item-lead-time"
                value={leadTimeText}
                onChange={(e) => setLeadTimeText(e.target.value)}
                placeholder="e.g. 2 days, or unknown — expect 2-4 weeks"
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-lead-worst">Worst-case lead (days)</Label>
              <Input
                id="item-lead-worst"
                value={leadTimeWorstCase}
                onChange={(e) => setLeadTimeWorstCase(e.target.value)}
                placeholder="e.g. 28"
                inputMode="numeric"
                className="tabular-nums"
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="item-description">Description</Label>
              <Textarea
                id="item-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short notes that show up on the detail page."
                rows={3}
                maxLength={2000}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="item-notes">Notes</Label>
              <Textarea
                id="item-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="General comments about this item."
                rows={3}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
